import { useState, useEffect, useMemo } from 'react';
import { Popup, Button, Input, Switch, InputNumber, Collapse, CollapseItem, Picker, Checkbox, Radio } from '@nutui/nutui-react';
import { buildPickerOptions } from '../teamConfig';

/**
 * 阵容编辑/新建表单组件
 * 使用底部弹窗（Popup）展示，支持编辑阵容基本信息和5个位置的配置
 */
function TeamEditor({ visible, onClose, onSave, team, configEnums, presetTeamList }) {
    const [formData, setFormData] = useState(null);
    const [saving, setSaving] = useState(false);

    // Picker 可见状态
    const [weaponPickerVisible, setWeaponPickerVisible] = useState(false);
    const [heroPickerVisible, setHeroPickerVisible] = useState({ visible: false, slot: null });
    const [artifactPickerVisible, setArtifactPickerVisible] = useState({ visible: false, slot: null });
    const [pearlSkillPickerVisible, setPearlSkillPickerVisible] = useState({ visible: false, slot: null });

    // 位置配置展开状态
    const [activeSlotKey, setActiveSlotKey] = useState([]);

    // 弹窗打开时初始化表单数据
    useEffect(() => {
        if (visible) {
            if (team) {
                const copy = JSON.parse(JSON.stringify(team));
                // teamId 不在当前可用预设列表内时归一化为“不指定”（如 VIP 降级、presetTeamInfo 变化）
                if (copy.teamId && !(presetTeamList || []).some(p => p.value === copy.teamId)) {
                    copy.teamId = undefined;
                }
                setFormData(copy);
            } else {
                setFormData({
                    name: '',
                    presetTeamName: '',
                    teamId: undefined,
                    weaponId: undefined,
                    public: false,
                    slots: {},
                });
            }
            // 重置展开状态
            setActiveSlotKey([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, team]);

    // 构建 Picker 选项
    const weaponOptions = useMemo(() => {
        if (!configEnums?.weaponTypes) return [];
        return buildPickerOptions(configEnums.weaponTypes);
    }, [configEnums]);

    const heroOptions = useMemo(() => {
        if (!configEnums?.heroes) return [];
        return buildPickerOptions(configEnums.heroes);
    }, [configEnums]);

    const artifactOptions = useMemo(() => {
        if (!configEnums?.artifactTypes) return [];
        return buildPickerOptions(configEnums.artifactTypes, {
            prepend: [
                { value: -1, text: '不操作' },
                { value: 0, text: '卸载' },
            ],
        });
    }, [configEnums]);

    const pearlSkillOptions = useMemo(() => {
        if (!configEnums?.pearlSkills) return [];
        return buildPickerOptions(configEnums.pearlSkills, {
            prepend: [{ value: -1, text: '不操作' }],
        });
    }, [configEnums]);

    const attrOptions = useMemo(() => {
        if (!configEnums?.unifiedAttributes) return [];
        return Object.entries(configEnums.unifiedAttributes)
            .filter(([value]) => parseInt(value) >= 5) // 过滤掉 None和绝对值属性
            .map(([value, text]) => ({
                value: parseInt(value),
                text: String(text),
            }));
    }, [configEnums]);

    if (!formData) return null;

    const updateField = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const updateSlotField = (slotKey, field, value) => {
        setFormData(prev => ({
            ...prev,
            slots: {
                ...prev.slots,
                [slotKey]: {
                    ...prev.slots[slotKey],
                    [field]: value,
                },
            },
        }));
    };

    const toggleSlot = (slotKey, enabled) => {
        setFormData(prev => {
            const newSlots = { ...prev.slots };
            if (enabled) {
                newSlots[slotKey] = {
                    slot: slotKey,
                    heroId: 0,
                    sequence: slotKey,
                    keyAttrs: [],
                    minHeroLevel: 0,
                    maxHeroLevel: 0,
                    minEquipLevel: 0,
                    attachmentUid: undefined,
                    boundAttachment: false,
                };
            } else {
                delete newSlots[slotKey];
            }
            return { ...prev, slots: newSlots };
        });
    };

    const toggleAttr = (slotKey, attrValue) => {
        const slot = formData.slots[slotKey];
        if (!slot) return;
        const attrs = slot.keyAttrs || [];
        const newAttrs = attrs.includes(attrValue) ? attrs.filter(a => a !== attrValue) : [...attrs, attrValue];
        updateSlotField(slotKey, 'keyAttrs', newAttrs);
    };

    const handleSave = async () => {
        if (!formData.name?.trim()) {
            alert('请输入阵容名称');
            return;
        }
        setSaving(true);
        try {
            await onSave(formData);
        } finally {
            setSaving(false);
        }
    };

    // 获取 Picker 选中值对应的文字
    const getPickerText = (options, value) => {
        if (value === undefined || value === null) return '未设置';
        const opt = options.find(o => o.value === value);
        return opt ? opt.text : `未知(${value})`;
    };

    return (
        <Popup
            closeable
            title={team ? '编辑阵容' : '新建阵容'}
            visible={visible}
            style={{ height: '85%' }}
            position='bottom'
            onClose={onClose}>
            <div style={{ padding: '10px 16px', overflow: 'auto', height: 'calc(100% - 90px)' }}>
                {/* 基本信息 */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>基本信息</div>

                    {/* 阵容名称 */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', color: '#333' }}>阵容名称</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>用于识别和区分阵容，仅在脚本中使用</span>
                        </div>
                        <Input
                            placeholder='请输入阵容名称'
                            value={formData.name}
                            onChange={val => updateField('name', val)}
                            style={{ '--nutui-input-background-color': '#f7f8fa' }}
                        />
                    </div>

                    {/* 预设名称 */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', color: '#333' }}>预设名称</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>保存到游戏内的名称，最多3个汉字</span>
                        </div>
                        <Input
                            placeholder='最多3个汉字'
                            maxLength={10}
                            value={formData.presetTeamName || ''}
                            onChange={val => updateField('presetTeamName', val)}
                            style={{ '--nutui-input-background-color': '#f7f8fa' }}
                        />
                    </div>

                    {/* 默认预设阵容槽位 */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', color: '#333' }}>默认预设阵容</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>应用阵容时默认选中的预设阵容槽位</span>
                        </div>
                        <Radio.Group
                            value={String(formData.teamId || 0)}
                            onChange={val => updateField('teamId', parseInt(val) > 0 ? parseInt(val) : undefined)}>
                            <Radio value='0' style={{ marginBottom: '4px' }}>不指定</Radio>
                            {(presetTeamList || []).map(preset => (
                                <Radio key={preset.value} value={String(preset.value)} style={{ marginBottom: '4px' }}>
                                    {preset.name ? `阵容${preset.value}：${preset.name}` : `阵容${preset.value}`}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </div>

                    {/* 玩具 */}
                    <div
                        style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer'
                        }}
                        onClick={() => setWeaponPickerVisible(true)}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '14px', color: '#333' }}>玩具</div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>选择阵容使用的玩具</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontSize: '14px', color: formData.weaponId !== undefined ? '#333' : '#999' }}>
                                    {getPickerText(weaponOptions, formData.weaponId)}
                                </span>
                                <span style={{ color: '#999', fontSize: '12px' }}>›</span>
                            </div>
                        </div>
                    </div>

                    {/* 公开 */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '14px', color: '#333' }}>公开</div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>其他角色可以复制此阵容</div>
                            </div>
                            <Switch checked={formData.public || false} onChange={val => updateField('public', val)} />
                        </div>
                    </div>
                </div>

                {/* 出战位置配置 */}
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>出战位置</div>
                    <Collapse
                        activeKey={activeSlotKey}
                        onChange={(key) => setActiveSlotKey(key ? [key] : [])}
                    >
                        {[0, 1, 2, 3, 4].map(slotKey => {
                            const slot = formData.slots[slotKey];
                            const isEnabled = !!slot;
                            return (
                                <CollapseItem
                                    key={slotKey}
                                    name={slotKey}
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>
                                                位置{slotKey + 1}
                                                {isEnabled && slot.heroId
                                                    ? `：${getPickerText(heroOptions, slot.heroId)}`
                                                    : ''}
                                                {isEnabled && (slot.boundAttachment ? ' 已绑定装备' : ` 装备优先级：${slot.sequence ?? 0}`)}
                                            </span>
                                            {!isEnabled && (
                                                <span style={{ fontSize: '12px', color: '#999' }}>(未出战)</span>
                                            )}
                                        </div>
                                    }>
                                    {/* 启用 */}
                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', color: '#333' }}>出战</div>
                                                <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>禁用后此位置空缺</div>
                                            </div>
                                            <Switch checked={isEnabled} onChange={val => toggleSlot(slotKey, val)} />
                                        </div>
                                    </div>
                                    {isEnabled && (
                                        <>
                                            {/* 咸将 */}
                                            <div
                                                style={{
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setHeroPickerVisible({ visible: true, slot: slotKey })}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <div style={{ fontSize: '14px', color: '#333' }}>咸将</div>
                                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}></div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '14px', color: slot.heroId ? '#333' : '#999' }}>
                                                            {getPickerText(heroOptions, slot.heroId)}
                                                        </span>
                                                        <span style={{ color: '#999', fontSize: '12px' }}>›</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* 绑定洗练 */}
                                            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Checkbox
                                                        checked={!!slot.boundAttachment}
                                                        disabled={!slot.attachmentUid || slot.attachmentUid <= 0}
                                                        onChange={(val) => {
                                                            // 如果attachmentUid无效，不允许设置为true
                                                            if (val && (!slot.attachmentUid || slot.attachmentUid <= 0)) {
                                                                return;
                                                            }
                                                            updateSlotField(slotKey, 'boundAttachment', val);
                                                        }}
                                                    >
                                                        绑定装备（洗练+水晶）
                                                    </Checkbox>
                                                    {(!slot.attachmentUid || slot.attachmentUid <= 0) && (
                                                        <span style={{ fontSize: '12px', color: '#999' }}>
                                                            只有通过"加载预设阵容"获取的阵容才可以绑定装备
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* 以下控件在绑定洗练时隐藏 */}
                                            {!slot.boundAttachment && (
                                                <>
                                                    {/* 装备优先级 */}
                                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '14px', color: '#333' }}>装备优先级</span>
                                                            <span style={{ fontSize: '12px', color: '#999' }}>数值越小优先级越高，0-10。</span>
                                                        </div>
                                                        <InputNumber
                                                            min={0}
                                                            max={10}
                                                            value={slot.sequence}
                                                            onChange={val => updateSlotField(slotKey, 'sequence', val)}
                                                            style={{ '--nutui-inputnumber-input-background-color': '#f7f8fa' }}
                                                        />
                                                    </div>
                                                    {/* 咸将最低等级 */}
                                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '14px', color: '#333' }}>咸将最低等级</span>
                                                            <span style={{ fontSize: '12px', color: '#999' }}>咸将等级下限，0表示不限制</span>
                                                        </div>
                                                        <InputNumber
                                                            min={0}
                                                            max={99999}
                                                            value={slot.minHeroLevel || 0}
                                                            onChange={val => updateSlotField(slotKey, 'minHeroLevel', val)}
                                                            style={{ '--nutui-inputnumber-input-background-color': '#f7f8fa' }}
                                                        />
                                                    </div>
                                                    {/* 咸将最高等级 */}
                                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '14px', color: '#333' }}>咸将最高等级</span>
                                                            <span style={{ fontSize: '12px', color: '#999' }}>咸将等级上限，0表示不限制</span>
                                                        </div>
                                                        <InputNumber
                                                            min={0}
                                                            max={99999}
                                                            value={slot.maxHeroLevel || 0}
                                                            onChange={val => updateSlotField(slotKey, 'maxHeroLevel', val)}
                                                            style={{ '--nutui-inputnumber-input-background-color': '#f7f8fa' }}
                                                        />
                                                    </div>
                                                    {/* 装备最低等级 */}
                                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '14px', color: '#333' }}>装备最低等级</span>
                                                            <span style={{ fontSize: '12px', color: '#999' }}>装备等级下限，0表示不限制</span>
                                                        </div>
                                                        <InputNumber
                                                            min={0}
                                                            max={99999}
                                                            value={slot.minEquipLevel || 0}
                                                            onChange={val => updateSlotField(slotKey, 'minEquipLevel', val)}
                                                            style={{ '--nutui-inputnumber-input-background-color': '#f7f8fa' }}
                                                        />
                                                    </div>
                                                    {/* 关键属性多选 */}
                                                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                            <span style={{ fontSize: '14px', color: '#333' }}>关键属性</span>
                                                            <span style={{ fontSize: '12px', color: '#999' }}>选择装备洗练的关键属性</span>
                                                        </div>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: '6px',
                                                            }}>
                                                            {attrOptions.map(attr => {
                                                                const selected = (slot.keyAttrs || []).includes(attr.value);
                                                                return (
                                                                    <Button
                                                                        key={attr.value}
                                                                        size='mini'
                                                                        type={selected ? 'primary' : 'default'}
                                                                        onClick={() => toggleAttr(slotKey, attr.value)}>
                                                                        {attr.text}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {/* 鱼灵 */}
                                            <div
                                                style={{
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setArtifactPickerVisible({ visible: true, slot: slotKey })}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <div style={{ fontSize: '14px', color: '#333' }}>鱼灵</div>
                                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>选择此位置的鱼灵</div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '14px', color: slot.artifactId !== undefined ? '#333' : '#999' }}>
                                                            {slot.artifactId === undefined ? '不操作' : getPickerText(artifactOptions, slot.artifactId)}
                                                        </span>
                                                        <span style={{ color: '#999', fontSize: '12px' }}>›</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* 鱼珠技能 */}
                                            <div
                                                style={{
                                                    padding: '12px 16px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setPearlSkillPickerVisible({ visible: true, slot: slotKey })}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <div style={{ fontSize: '14px', color: '#333' }}>鱼珠技能</div>
                                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>选择此位置的鱼珠技能</div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span style={{ fontSize: '14px', color: slot.pearlSkillId !== undefined ? '#333' : '#999' }}>
                                                            {slot.pearlSkillId === undefined ? '不操作' : getPickerText(pearlSkillOptions, slot.pearlSkillId)}
                                                        </span>
                                                        <span style={{ color: '#999', fontSize: '12px' }}>›</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </CollapseItem>
                            );
                        })}
                    </Collapse>
                </div>
            </div>

            {/* 底部保存/取消按钮 */}
            <div
                style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '10px 16px',
                    borderTop: '1px solid #eee',
                    justifyContent: 'flex-end',
                }}>
                <Button size='small' onClick={onClose}>
                    取消
                </Button>
                <Button size='small' type='primary' loading={saving} onClick={handleSave}>
                    保存
                </Button>
            </div>

            {/* 玩具 Picker */}
            <Picker
                visible={weaponPickerVisible}
                options={[weaponOptions]}
                value={formData.weaponId !== undefined ? [formData.weaponId] : []}
                onConfirm={(values) => {
                    if (values && values[0] !== undefined) {
                        updateField('weaponId', values[0].value);
                    }
                    setWeaponPickerVisible(false);
                }}
                onClose={() => setWeaponPickerVisible(false)}
            />

            {/* 咸将 Picker */}
            <Picker
                visible={heroPickerVisible.visible}
                options={[heroOptions]}
                value={
                    heroPickerVisible.slot !== null && formData.slots[heroPickerVisible.slot]
                        ? [formData.slots[heroPickerVisible.slot].heroId]
                        : []
                }
                onConfirm={(values) => {
                    if (values && values[0] !== undefined && heroPickerVisible.slot !== null) {
                        updateSlotField(heroPickerVisible.slot, 'heroId', values[0].value);
                    }
                    setHeroPickerVisible({ visible: false, slot: null });
                }}
                onClose={() => setHeroPickerVisible({ visible: false, slot: null })}
            />

            {/* 鱼灵 Picker */}
            <Picker
                visible={artifactPickerVisible.visible}
                options={[artifactOptions]}
                value={
                    artifactPickerVisible.slot !== null && formData.slots[artifactPickerVisible.slot]
                        ? [formData.slots[artifactPickerVisible.slot].artifactId ?? -1]
                        : []
                }
                onConfirm={(values) => {
                    if (values && values[0] !== undefined && artifactPickerVisible.slot !== null) {
                        const val = values[0].value;
                        updateSlotField(artifactPickerVisible.slot, 'artifactId', val === -1 ? undefined : val);
                    }
                    setArtifactPickerVisible({ visible: false, slot: null });
                }}
                onClose={() => setArtifactPickerVisible({ visible: false, slot: null })}
            />

            {/* 鱼珠技能 Picker */}
            <Picker
                visible={pearlSkillPickerVisible.visible}
                options={[pearlSkillOptions]}
                value={
                    pearlSkillPickerVisible.slot !== null && formData.slots[pearlSkillPickerVisible.slot]
                        ? [formData.slots[pearlSkillPickerVisible.slot].pearlSkillId ?? -1]
                        : []
                }
                onConfirm={(values) => {
                    if (values && values[0] !== undefined && pearlSkillPickerVisible.slot !== null) {
                        const val = values[0].value;
                        updateSlotField(pearlSkillPickerVisible.slot, 'pearlSkillId', val === -1 ? undefined : val);
                    }
                    setPearlSkillPickerVisible({ visible: false, slot: null });
                }}
                onClose={() => setPearlSkillPickerVisible({ visible: false, slot: null })}
            />
        </Popup>
    );
}

export default TeamEditor;
